import React, { useEffect, useState } from 'react';
import SimilarListings, {
  SimilarListingsComponentUsage,
} from 'components/dynamic-page/listing-page/similar-listings-sections/similar-listings';
import { useInView } from 'react-intersection-observer';
import { listingPageModalIds } from 'constants/test-constants';
import type Listing from 'data/listing';
import { getSimilarListings } from 'data/listing';
import {
  SimilarListingCardData,
} from 'components/dynamic-page/listing-page/similar-listings-sections/similar_listing_card_data';
import styles from './style.module.scss';

import type { SimilarListing } from '@zoocasa/go-search';

interface Props {
  listing: SimilarListingCardData | Listing;
  isOffMarket?: boolean;
  hasNavigatedFromInternalLink?: boolean;
  isTop?: boolean;
}

type SimilarListingState = {
  similarListings: SimilarListingCardData[] | null[];
  similarSoldListings: SimilarListingCardData[] | null[];
  isLoading: boolean;
};

const emptyStateArray = [null, null, null, null, null, null];

export const similarListingToSimilarListingCardData = (listing: SimilarListing) => (new SimilarListingCardData(listing));

export default function SimilarListingsSections({ listing, isOffMarket, hasNavigatedFromInternalLink, isTop }: Props) {
  const [similarListingState, setSimilarListingState] = useState<SimilarListingState>({
    similarListings: emptyStateArray,
    similarSoldListings: emptyStateArray,
    isLoading: true,
  });
  const { ref, inView } = useInView();
  const hideSimilar = (!hasNavigatedFromInternalLink && !isTop) || !similarListingState.isLoading && similarListingState.similarListings.length === 0;
  const hideSold = (!hasNavigatedFromInternalLink && isTop) || !similarListingState.isLoading && similarListingState.similarSoldListings.length === 0;

  const setAllSimilarListings = async () => {
    if (inView && similarListingState.isLoading) {
      const [similarListings, similarSoldListings] = await Promise.all([
        getSimilarListings(listing.id, 6),
        getSimilarListings(listing.id, 6, 'not-available-sold'),
      ]);
      const formattedSimilarListings = similarListings.map((listing: SimilarListing) => similarListingToSimilarListingCardData(listing));
      const formattedSoldSimilarListings = similarSoldListings.map((listing: SimilarListing) => similarListingToSimilarListingCardData(listing));
      setSimilarListingState({
        similarListings: formattedSimilarListings,
        similarSoldListings: formattedSoldSimilarListings,
        isLoading: false,
      });
    }
  };

  useEffect(() => {
    setAllSimilarListings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <div className={ styles.component } ref={ ref } data-testid={listingPageModalIds.similarListingsSections}>
      {!hideSimilar && <SimilarListings
        heading="Similar Listings"
        componentUsage={SimilarListingsComponentUsage.Similar}
        isLoading={similarListingState.isLoading}
        listings={similarListingState.similarListings}
        hasNavigatedFromInternalLink={hasNavigatedFromInternalLink}
        isTop={isTop}
      />}
      {!hideSold && <SimilarListings
        heading="Comparable Properties"
        componentUsage={SimilarListingsComponentUsage.Comparable}
        isLoading={similarListingState.isLoading}
        listingToCompare={!isOffMarket ? listing : undefined}
        listings={similarListingState.similarSoldListings}
      />}
    </div>
  );
}
